import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  MAX_DELIVERY_SCHEDULES,
  MAX_DELIVERY_SCHEDULE_DAYS,
  type DeliverySchedule,
} from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, Input, Label, useToast } from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import {
  useDeliverySchedules,
  useSaveDeliverySchedules,
} from '../lib/settings/use-delivery-schedules.js';

/**
 * The speeds this shop offers: "Express, 3 days", "Normal, 7 days".
 *
 * The list is edited as a list and saved in one go, the way working hours are — a shop sits down
 * with these once and adds a row, and a per-row save would mean four confirmations for one
 * decision.
 *
 * What a schedule DOES is fill in a due date at the counter, nothing more. Changing "Express"
 * from three days to five changes no order already booked, because a booked order holds the date
 * it was promised, not the schedule the date came from. That is stated on the screen: an owner
 * editing these should know they are not moving anybody's promise.
 */

interface Row {
  name: string;
  /** Kept as typed. A half-deleted number is a string, and refusing to render it is worse. */
  days: string;
}

const BLANK: Row = { name: '', days: '7' };

function rowsFrom(schedules: readonly DeliverySchedule[]): Row[] {
  return schedules.map((schedule) => ({ name: schedule.name, days: String(schedule.days) }));
}

export function DeliverySchedulesPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();

  const schedules = useDeliverySchedules();
  const save = useSaveDeliverySchedules();

  /** What has been typed. Null until somebody touches the list, so the query stays in charge. */
  const [draft, setDraft] = useState<Row[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const rows = draft ?? rowsFrom(schedules.data ?? []);

  const edit = (index: number, patch: Partial<Row>): void => {
    setRefusal(null);
    setDraft(rows.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  };

  const submit = async (): Promise<void> => {
    setRefusal(null);

    // Blank rows are dropped rather than refused: an owner who added a row and changed their
    // mind should be able to save without hunting for the one they left empty.
    const filled = rows.filter((row) => row.name.trim() !== '');
    const payload: DeliverySchedule[] = filled.map((row) => ({
      name: row.name.trim(),
      days: Number(row.days) || 0,
    }));

    const names = payload.map((schedule) => schedule.name.toLowerCase());
    if (new Set(names).size !== names.length) {
      setRefusal(t('settings.deliverySchedules.duplicateName'));
      return;
    }
    if (
      payload.some((schedule) => schedule.days < 0 || schedule.days > MAX_DELIVERY_SCHEDULE_DAYS)
    ) {
      setRefusal(t('settings.deliverySchedules.badDays', { max: MAX_DELIVERY_SCHEDULE_DAYS }));
      return;
    }

    try {
      const saved = await save.mutateAsync(payload);
      setDraft(rowsFrom(saved));
      toast({ title: t('settings.deliverySchedules.saved'), variant: 'success' });
    } catch (caught) {
      setRefusal(isApiRequestError(caught) ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <>
      <PageHeader
        title={t('settings.deliverySchedules.title')}
        description={t('settings.deliverySchedules.description')}
      />

      <div className="flex flex-col gap-4">
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            {t('settings.deliverySchedules.empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {rows.map((row, index) => (
              <li
                key={index}
                className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-end"
              >
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor={`schedule-name-${index}`}>
                    {t('settings.deliverySchedules.name')}
                  </Label>
                  <Input
                    id={`schedule-name-${index}`}
                    value={row.name}
                    placeholder={t('settings.deliverySchedules.namePlaceholder')}
                    onChange={(event) => edit(index, { name: event.target.value })}
                  />
                </div>

                <div className="flex flex-col gap-1.5 sm:w-32">
                  <Label htmlFor={`schedule-days-${index}`}>
                    {t('settings.deliverySchedules.days')}
                  </Label>
                  <Input
                    id={`schedule-days-${index}`}
                    inputMode="numeric"
                    value={row.days}
                    onChange={(event) => edit(index, { days: event.target.value })}
                  />
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('settings.deliverySchedules.remove', {
                    name: row.name || index + 1,
                  })}
                  onClick={() => {
                    setRefusal(null);
                    setDraft(rows.filter((_, position) => position !== index));
                  }}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {refusal ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {refusal}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            disabled={rows.length >= MAX_DELIVERY_SCHEDULES}
            onClick={() => {
              setRefusal(null);
              setDraft([...rows, { ...BLANK }]);
            }}
          >
            <Plus aria-hidden="true" className="size-4" />
            {t('settings.deliverySchedules.add')}
          </Button>

          <Button disabled={save.isPending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>

        {/* The thing an owner most needs to know before editing this screen. */}
        <p className="text-sm text-muted-foreground">
          {t('settings.deliverySchedules.doesNotMoveBookedOrders')}
        </p>
      </div>
    </>
  );
}
